import * as zod from 'zod';

import { CameraIntegration } from '@sb/integrations/types/cameraTypes';
import { ActionRequiredError, Step } from '@sb/remote-control/types';
import {
  DEFAULT_REGION_OF_INTEREST,
  RegionOfInterest,
} from '@sb/routine-runner';
import { ClassifyMethod } from '@sb/routine-runner/Step/Classify/Arguments';

export namespace ClassifyStep {
  export const name = 'Classify';
  export const description = 'Classify a state via vision';
  export const librarySection = Step.LibrarySection.Vision;
  export const argumentKind = 'Classify';
  export const featureFlag = 'classifyStep';

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    camera: CameraIntegration.optional(),
    cameraEquipmentId: zod.string().optional(),
    regionOfInterest: RegionOfInterest.default(DEFAULT_REGION_OF_INTEREST),
    method: ClassifyMethod.default({ kind: 'TemplateMatch', classes: [] }),
    confidence: zod.number().default(0.5),
    fallbackValue: zod.string().default('No class'),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    if (!args.fallbackValue) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Fallback value is not configured',
        fieldId: 'fallbackValue',
      });
    }

    return {
      ...stepData,
      stepKind: 'Classify',
      args: {
        regionOfInterest: args.regionOfInterest,
        method: args.method,
        confidence: args.confidence,
        camera: args.camera,
        cameraEquipmentId: args.cameraEquipmentId,
        fallbackValue: args.fallbackValue,
      },
    };
  };
}

ClassifyStep satisfies Step.StepKindInfo;
